import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Switch } from "@component/switch";
import { Textarea } from "@component/textarea";
import { AlertTriangleIcon, CheckIcon, Loader2Icon, MessageCircleIcon, ZapIcon } from "lucide-react";
import { getTriggerStepPreview, triggerFlowStep, TriggerStepPreviewResult, TriggerStepPreviewStep, TriggerStepResult } from "@service/hiring-flow";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

interface FlowStepLite {
    id: string;
    type: string;
    label?: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Application id — the flow endpoints take this, not the applicant id. */
    applicationId: string;
    /** Fallback step source (job payload) while the preview read is absent. */
    steps: FlowStepLite[];
    stepLabel: (step: FlowStepLite) => string;
    /** 1-based index of the candidate's current step; null once the flow is done. */
    currentStepNo: number | null;
    /** Called with the BE result — the parent owns toasts + thread refresh. */
    onTriggered: (result: TriggerStepResult, stepLabel: string) => void;
}

interface DisplayStep {
    id: string;
    label: string;
    type?: string;
    state: "completed" | "current" | "upcoming";
    // Mirrors the service type so the shapes can't drift apart again.
    preview?: TriggerStepPreviewStep["preview"];
}

/**
 * HR "Follow up" picker (#3242/#3334, BE ourteam-backend#3981/#4179). HR picks
 * a step and Maya (re-)opens it in the candidate's WhatsApp thread —
 * immediately when the candidate messaged <24h ago, otherwise via an approved
 * re-engage template that arms the step until the candidate replies.
 *
 * The step list + per-step message preview come from the dedicated dry-run
 * read (#4179 — built from the application's PINNED flow version, so an
 * older-version candidate sees their real steps). While that endpoint is
 * absent (older BE) the dialog falls back to the job-payload steps with no
 * preview panel — exactly the pre-#3334 behaviour.
 */
export default function TriggerStepDialog({ isOpen, onClose, applicationId, steps, stepLabel, currentStepNo, onTriggered }: Props) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [note, setNote] = useState<string>("");
    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<TriggerStepPreviewResult | null>(null);
    const [isLoadingPreview, setLoadingPreview] = useState<boolean>(false);
    // #3334 follow-up: the message preview lives in its own WA-look popup
    // (same layout as the flow preview dialog), not inline in this dialog.
    const [showWaPreview, setShowWaPreview] = useState<boolean>(false);
    // HR's explicit send-mode choice — the toggle position directly: ON goes
    // through the re-engage template leg, OFF sends the in-window (24h)
    // message. Defaults from the BE's in_session when the read lands.
    // NOTE: the payload field is named is_send_24hour but carries THIS value
    // as-is (true = template leg) — inverted name kept per product decision.
    const [sendByTemplate, setSendByTemplate] = useState<boolean>(false);
    // #3378/#3386: AI Interview action — "continue" = today's behavior;
    // "move_to_phone_call" / "retry_call" = their invitation template
    // (template-only send, no LLM leg). Reset whenever the selection changes.
    const [interviewAction, setInterviewAction] = useState<"continue" | "move_to_phone_call" | "retry_call" | "_invalid_system_follow_up">("continue");
    // Guard against a reopen-for-another-candidate race landing a stale read.
    const previewRequestRef = useRef(0);

    // #3334: fetch the dry-run read on every open. Failure/absence degrades to
    // the job-payload fallback silently — no user-facing error.
    useEffect(() => {
        if (!isOpen || !applicationId) return;
        const requestId = ++previewRequestRef.current;
        setLoadingPreview(true);
        setPreviewData(null);
        getTriggerStepPreview(applicationId)
            .then((data) => {
                if (requestId !== previewRequestRef.current) return;
                if (data && Array.isArray(data.steps) && data.steps.length > 0) {
                    setPreviewData(data);
                    setSendByTemplate(data.in_session === false);
                }
            })
            .catch((err) => {
                if (requestId !== previewRequestRef.current) return;
                catchErrorLog(`[trigger-step-preview] read unavailable, using job-payload fallback: ${err}`);
            })
            .finally(() => {
                if (requestId === previewRequestRef.current) setLoadingPreview(false);
            });
    }, [isOpen, applicationId]);

    // One display model for both sources; the BE read wins when present.
    const displaySteps: DisplayStep[] = previewData
        ? previewData.steps.map((s) => ({ id: s.step_id, label: s.label, type: s.type, state: s.state, preview: s.preview }))
        : steps.map((s, i) => {
            const stepNo = i + 1;
            const state: DisplayStep["state"] =
                currentStepNo === null || stepNo < currentStepNo ? "completed"
                    : stepNo === currentStepNo ? "current" : "upcoming";
            return { id: s.id, label: stepLabel(s), type: s.type, state };
        });

    // Default-select the current step on open; re-align when the BE read lands
    // (its current/step ids are authoritative — pinned version, not the job's).
    useEffect(() => {
        if (!isOpen) return;
        setNote("");
        setError(null);
        setSelectedId(displaySteps.find((s) => s.state === "current")?.id ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, previewData, currentStepNo]);

    // Any selection change falls back to Continue — the phone-call variant is
    // an explicit per-step choice, never sticky across steps/reopens.
    useEffect(() => {
        setInterviewAction("continue");
    }, [selectedId]);

    const currentIndex = displaySteps.findIndex((s) => s.state === "current");
    const selectedIndex = selectedId ? displaySteps.findIndex((s) => s.id === selectedId) : -1;
    const selectedStep = selectedIndex >= 0 ? displaySteps[selectedIndex] : undefined;
    const isForwardPick = currentIndex !== -1 && selectedIndex > currentIndex && !["_general_follow_up", "_invalid_system_follow_up"];
    // A completed step can be re-triggered — Maya simply asks it again; flag it
    // so HR knows they're re-opening finished work.
    const isCompletedPick = selectedStep?.state === "completed";
    // Outside the 24h window WhatsApp forbids free-form — the template leg is
    // the only legal send, so the toggle is forced ON and locked. Only when
    // the read is present (fallback mode has no window info, toggle stays free).
    const templateRequired = previewData?.in_session === false;
    // #3378/#3386: the phone-call and retry-call variants are pure template
    // sends — either locks the send-mode toggle ON just like the
    // out-of-window rule does.
    const isAiInterviewPick = selectedStep?.type === "ai_interview";
    const phoneCallPick = isAiInterviewPick && interviewAction === "move_to_phone_call";
    const retryCallPick = isAiInterviewPick && interviewAction === "retry_call";
    const invalidSystem = selectedId === "_invalid_system_follow_up";
    const templateOnlyPick = phoneCallPick || retryCallPick;
    const effectiveSendByTemplate = templateOnlyPick || templateRequired || sendByTemplate || invalidSystem;

    const handleSubmit = () => {
        if (!applicationId || !selectedId) return;
        setSubmitting(true);
        setError(null);
        triggerFlowStep(applicationId, {
            step_id: selectedId,
            is_send_24hour: effectiveSendByTemplate,
            ...(isAiInterviewPick ? { interview_action: interviewAction } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
        })
            .then((result) => {
                const step = displaySteps.find((s) => s.id === (result.step_id || selectedId));
                onTriggered(result, step?.label || "the step");
                onClose();
            })
            .catch((err) => {
                // Surface the BE reason verbatim (409 gate-held / no active flow,
                // dispatch gates, Twilio) — masking it would hide the real fix.
                const beMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
                catchErrorLog(`failed to trigger flow step: ${err}`);
                setError(beMessage || "Couldn't trigger the step. Please try again.");
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSubmitting) onClose(); }}>
            <DialogContent className="sm:max-w-lg border-0 outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ZapIcon className="w-5 h-5 text-blue-600" />
                        Follow up
                    </DialogTitle>
                    <DialogDescription>
                        Maya nudges the candidate on WhatsApp and re-opens the step you pick. If their last message is older than 24 hours, a short template goes out first and Maya continues once they reply.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Step</p>
                    {isLoadingPreview && displaySteps.length === 0 ? (
                        <div className="space-y-1">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {displaySteps.map((step, i) => {
                                const isDone = step.state === "completed";
                                const isCurrent = step.state === "current";
                                const isSelected = selectedId === step.id;
                                return (
                                    <button
                                        key={step.id || i}
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setSelectedId(step.id)}
                                        className={cn(
                                            "flex w-full items-center gap-2.5 rounded-lg border p-2.5 text-left transition-colors",
                                            isSelected
                                                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                                                : "border-gray-200 dark:border-gray-700 hover:border-blue-300",
                                        )}
                                    >
                                        <span className={cn(
                                            "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                                            isDone ? "bg-green-100 text-green-700"
                                                : isCurrent ? "bg-blue-600 text-white"
                                                    : "bg-gray-100 text-gray-500",
                                        )}>
                                            {isDone ? <CheckIcon className="w-3 h-3" /> : i + 1}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-gray-100">{step.label}</span>
                                        {!["_general_follow_up", "_invalid_system_follow_up"].includes(step.id) ? (
                                            <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                                {isDone ? "Completed" : isCurrent ? "Current" : "Upcoming"}
                                            </span>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {isForwardPick && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                        <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        Steps in between will be marked as skipped by HR.
                    </div>
                )}
                {isCompletedPick && (
                    <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-800">
                        <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        This step is already completed — Maya will ask it again in the chat.
                    </div>
                )}

                {/* #3378/#3386: AI Interview action pills — Continue keeps
                    today's behavior; Move to Phone Call / Retry Call each send
                    their invitation template. */}
                {isAiInterviewPick && (
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">AI Interview action</p>
                        <div className="inline-flex items-center gap-1">
                            {([
                                { key: "continue" as const, label: "Continue" },
                                { key: "move_to_phone_call" as const, label: "Move to Phone Call" },
                                { key: "retry_call" as const, label: "Retry Call" },
                            ]).map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    disabled={isSubmitting}
                                    onClick={() => setInterviewAction(opt.key)}
                                    className={cn(
                                        "px-3 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap",
                                        interviewAction === opt.key
                                            ? "bg-blue-600 text-white"
                                            : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                    )}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {phoneCallPick && (
                            <p className="text-[11px] leading-snug text-gray-400">
                                Sends the approved phone-call invitation template — no AI conversation follows.
                            </p>
                        )}
                        {retryCallPick && (
                            <p className="text-[11px] leading-snug text-gray-400">
                                Tells the candidate we couldn&apos;t reach them and asks when to call again — no AI conversation follows.
                            </p>
                        )}
                    </div>
                )}

                {/* Send-mode toggle, phrased template-first: ON = the re-engage
                    template leg, OFF = the direct 24h in-window message. */}
                <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Send by template</p>
                        <p className="text-[11px] leading-snug text-gray-400">
                            {templateOnlyPick
                                ? `${phoneCallPick ? "Move to Phone Call" : "Retry Call"} is a template-only send — no AI conversation, so the toggle is locked.`
                                : templateRequired
                                    ? "The candidate's last message is older than 24 hours — WhatsApp only allows the template here."
                                    : effectiveSendByTemplate
                                        ? "The re-engage template goes out first — Maya continues once the candidate replies."
                                        : "Maya sends the step message right away (24-hour session)."}
                        </p>
                    </div>
                    <Switch
                        checked={effectiveSendByTemplate}
                        onCheckedChange={setSendByTemplate}
                        disabled={isSubmitting || templateRequired || templateOnlyPick || invalidSystem}
                        title={templateOnlyPick
                            ? `${phoneCallPick ? "Move to Phone Call" : "Retry Call"} is a template-only send.`
                            : templateRequired ? "Outside the 24-hour window the template is required." : undefined}
                        className="flex-shrink-0 data-[state=checked]:bg-blue-600"
                    />
                </div>

                {/* #3334: the verbatim message preview opens in its own WA-look popup. */}
                {previewData && selectedStep && (
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 text-xs"
                            onClick={() => setShowWaPreview(true)}
                        >
                            <MessageCircleIcon className="w-3.5 h-3.5 text-green-600" />
                            Preview WhatsApp message
                        </Button>
                    </div>
                )}

                <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Note (optional)</p>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder="Why you're triggering this step — stored on the activity log."
                        className="resize-none text-sm"
                        disabled={isSubmitting}
                    />
                </div>

                {error && (
                    <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
                        <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedId}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                    >
                        {isSubmitting ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <ZapIcon className="w-4 h-4" />}
                        Send follow up
                    </Button>
                </div>
            </DialogContent>

            {/* #3334: WA-look popup for the selected step's verbatim message —
                same layout as the flow preview dialog (Maya header + bubbles). */}
            <Dialog open={showWaPreview} onOpenChange={(open) => { if (!open) setShowWaPreview(false); }}>
                <DialogContent className="sm:max-w-md overflow-hidden p-0 gap-0 border-0 outline-none focus:outline-none focus-visible:outline-none">
                    {/* WA chat header */}
                    <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
                        <img
                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                            alt="Maya"
                            className="h-8 w-8 rounded-full bg-white object-cover"
                        />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight">Maya</p>
                            <p className="text-[11px] leading-tight text-white/70">WhatsApp Business</p>
                        </div>
                    </div>
                    {/* Chat area — the selected step's outgoing message(s), verbatim */}
                    <div className="max-h-[60vh] min-h-[220px] space-y-3 overflow-y-auto bg-[#ECE5DD] px-4 py-6 dark:bg-[#0B141A]">
                        <div className="flex justify-center">
                            <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] text-gray-500 shadow-sm dark:bg-[#202C33] dark:text-gray-300">
                                Follow up — {selectedStep?.label}
                            </span>
                        </div>
                        {/* ONE bubble, matching the send-mode toggle: ON = the 24h
                            step message; OFF = the re-engage template. Never both. */}
                        {(() => {
                            const text = phoneCallPick
                                ? selectedStep?.preview?.move_to_phone_call?.text
                                : retryCallPick
                                    ? selectedStep?.preview?.retry_call?.text
                                    : effectiveSendByTemplate
                                        ? selectedStep?.preview?.outside_24h?.text
                                        : selectedStep?.preview?.within_24h?.text;
                            return text ? (
                                <div className="relative max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm dark:bg-[#202C33]">
                                    <p className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100">{text}</p>
                                    <p className="mt-1 text-right text-[10px] text-gray-400">09:41</p>
                                </div>
                            ) : (
                                <p className="py-6 text-center text-xs text-gray-500">No preview available for this step yet.</p>
                            );
                        })()}
                    </div>
                    <div className="border-t border-gray-200 bg-white px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900">
                        <p className="text-[11px] text-gray-500">
                            {phoneCallPick
                                ? "Preview — invites the candidate to switch to the phone-call interview (template only, no AI conversation)."
                                : retryCallPick
                                    ? "Preview — tells the candidate we couldn't reach them and asks when to call again (template only, no AI conversation)."
                                    : effectiveSendByTemplate
                                        ? "Preview — the re-engage template goes out first; Maya continues with the step once the candidate replies."
                                        : "Preview — the exact message the candidate receives on WhatsApp (24-hour session)."}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
